import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash, faEdit, faMicrochip, faPaperPlane, faBell, faBellSlash } from "@fortawesome/free-solid-svg-icons";
import api from "../lib/api.js";
import Modal from "../components/ui/Modal.jsx";
import { toast } from "../components/ui/Toast.jsx";
import { smartDate } from "../lib/dates.js";

const userSchema = z.object({
  email:                  z.string().email("Invalid email"),
  first_name:             z.string().optional(),
  last_name:              z.string().optional(),
  role:                   z.enum(["user", "admin"]),
  phone_number:           z.string().optional(),
  notifications_enabled:  z.boolean().optional(),
  active:                 z.boolean().optional(),
});

export default function Admin() {
  const qc = useQueryClient();
  const [addOpen, setAddOpen]       = useState(false);
  const [editUser, setEditUser]     = useState(null);
  const [assignUser, setAssignUser] = useState(null);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => api.get("users").json(),
  });

  const { data: allDevices = [] } = useQuery({
    queryKey: ["admin-devices"],
    queryFn: () => api.get("devices").json(),
  });

  const createMutation = useMutation({
    mutationFn: (d) => api.post("users", { json: { user: d } }).json(),
    onSuccess: () => { qc.invalidateQueries(["admin-users"]); setAddOpen(false); },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => api.patch(`users/${id}`, { json: { user: data } }).json(),
    onSuccess: () => { qc.invalidateQueries(["admin-users"]); setEditUser(null); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`users/${id}`),
    onSuccess: () => qc.invalidateQueries(["admin-users"]),
  });

  const inviteMutation = useMutation({
    mutationFn: (id) => api.post(`users/${id}/invite`).json(),
    onSuccess: (data) => toast(data.message || "Invitation sent"),
    onError: () => toast("Failed to send invitation", "error"),
  });

  const toggleNotifMutation = useMutation({
    mutationFn: ({ id, enabled }) => api.patch(`users/${id}`, { json: { user: { notifications_enabled: !enabled } } }).json(),
    onSuccess: () => qc.invalidateQueries(["admin-users"]),
  });

  if (isLoading) return <div className="p-6 data text-sm" style={{ color: "var(--color-muted)" }}>Loading…</div>;

  return (
    <div className="p-6 space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold data" style={{ color: "var(--color-white)" }}>Users</h1>
          <p className="data text-xs mt-1" style={{ color: "var(--color-muted)" }}>{users.length} users</p>
        </div>
        <button className="btn btn-cyan" onClick={() => setAddOpen(true)}>
          <FontAwesomeIcon icon={faPlus} /> Add User
        </button>
      </div>

      <div className="space-y-2">
        {users.map((u) => (
          <div key={u.id} className="card p-4">
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                {/* Desktop: name + inline badges */}
                <div className="hidden sm:block">
                  <div className="data font-semibold" style={{ color: "var(--color-white)" }}>
                    {u.full_name || u.email}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span style={{
                      fontSize: 10, letterSpacing: "0.15em", fontWeight: 700, textTransform: "uppercase",
                      fontFamily: "var(--font-mono)",
                      color: u.role === "admin" ? "var(--color-red)" : "var(--color-blue)",
                      border: `1px solid ${u.role === "admin" ? "rgba(255,51,85,0.4)" : "rgba(68,136,255,0.3)"}`,
                      borderRadius: 4, padding: "1px 6px",
                      background: u.role === "admin" ? "rgba(255,51,85,0.08)" : "rgba(68,136,255,0.08)",
                    }}>{u.role}</span>
                    {u.active === false && (
                      <span style={{
                        fontSize: 10, letterSpacing: "0.15em", fontWeight: 700, textTransform: "uppercase",
                        fontFamily: "var(--font-mono)", color: "var(--color-amber)",
                        border: "1px solid rgba(255,170,0,0.4)", borderRadius: 4, padding: "1px 6px",
                        background: "rgba(255,170,0,0.08)",
                      }}>DISABLED</span>
                    )}
                    {u.notifications_enabled === false && (
                      <span style={{
                        fontSize: 10, letterSpacing: "0.15em", fontWeight: 700, textTransform: "uppercase",
                        fontFamily: "var(--font-mono)", color: "var(--color-muted)",
                        border: "1px solid var(--color-border)", borderRadius: 4, padding: "1px 6px",
                      }}>NOTIF OFF</span>
                    )}
                  </div>
                  <div className="data text-xs mt-1 flex items-center gap-2" style={{ color: "var(--color-muted)" }}>
                    <span>{u.email}</span>
                    {u.phone_number && <><span style={{ opacity: 0.4 }}>·</span><span>{u.phone_number}</span></>}
                  </div>
                  <div className="data text-xs mt-0.5" style={{ color: "var(--color-muted)", opacity: 0.6 }}>
                    {u.last_sign_in_at ? `Last login: ${smartDate(u.last_sign_in_at)} · ${u.sign_in_count}×` : "Never logged in"}
                  </div>
                </div>
                {/* Mobile: stacked list */}
                <div className="sm:hidden space-y-1">
                  <div className="data font-semibold" style={{ color: "var(--color-white)" }}>
                    {u.full_name || u.email}
                  </div>
                  <div>
                    <span style={{
                      fontSize: 10, letterSpacing: "0.15em", fontWeight: 700, textTransform: "uppercase",
                      fontFamily: "var(--font-mono)",
                      color: u.role === "admin" ? "var(--color-red)" : "var(--color-blue)",
                      border: `1px solid ${u.role === "admin" ? "rgba(255,51,85,0.4)" : "rgba(68,136,255,0.3)"}`,
                      borderRadius: 4, padding: "1px 6px",
                      background: u.role === "admin" ? "rgba(255,51,85,0.08)" : "rgba(68,136,255,0.08)",
                    }}>{u.role}</span>
                    {u.active === false && (
                      <span className="ml-1" style={{
                        fontSize: 10, letterSpacing: "0.15em", fontWeight: 700, textTransform: "uppercase",
                        fontFamily: "var(--font-mono)", color: "var(--color-amber)",
                        border: "1px solid rgba(255,170,0,0.4)", borderRadius: 4, padding: "1px 6px",
                        background: "rgba(255,170,0,0.08)",
                      }}>DISABLED</span>
                    )}
                  </div>
                  {u.full_name && (
                    <div className="data text-xs flex items-center gap-2" style={{ color: "var(--color-muted)" }}>
                      <span>{u.email}</span>
                      {u.phone_number && <><span style={{ opacity: 0.4 }}>·</span><span>{u.phone_number}</span></>}
                    </div>
                  )}
                  <div className="data text-xs" style={{ color: "var(--color-muted)", opacity: 0.6 }}>
                    {u.last_sign_in_at ? `Last login: ${smartDate(u.last_sign_in_at)}` : "Never logged in"}
                  </div>
                </div>
              </div>
              <div className="hidden sm:flex gap-2">
                <button
                  className="btn btn-ghost"
                  style={{
                    padding: "4px 8px", fontSize: 12,
                    color: u.notifications_enabled ? "var(--color-blue)" : "var(--color-muted)",
                    background: u.notifications_enabled ? "rgba(68,136,255,0.12)" : undefined,
                  }}
                  onClick={() => toggleNotifMutation.mutate({ id: u.id, enabled: u.notifications_enabled })}
                  disabled={toggleNotifMutation.isPending}
                  title={u.notifications_enabled ? "Notifications ON — click to disable" : "Notifications OFF — click to enable"}
                >
                  <FontAwesomeIcon icon={u.notifications_enabled ? faBell : faBellSlash} />
                </button>
                <button className="btn btn-ghost" style={{ padding: "4px 8px", fontSize: 12 }}
                  onClick={() => setAssignUser(u)} title="Assign devices">
                  <FontAwesomeIcon icon={faMicrochip} />
                </button>
                <button className="btn btn-ghost" style={{ padding: "4px 8px", fontSize: 12 }}
                  onClick={() => setEditUser(u)} title="Edit">
                  <FontAwesomeIcon icon={faEdit} />
                </button>
                <button className="btn btn-danger" style={{ padding: "4px 8px", fontSize: 12 }}
                  onClick={() => { if (confirm(`Delete ${u.email}?`)) deleteMutation.mutate(u.id); }}>
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            </div>
            <div className="sm:hidden mt-3 pt-3 flex gap-2" style={{ borderTop: "1px solid var(--color-border)" }}>
              <button
                className="btn btn-ghost flex-1 justify-center"
                style={{
                  fontSize: 14, padding: "8px",
                  color: u.notifications_enabled ? "var(--color-blue)" : "var(--color-muted)",
                  background: u.notifications_enabled ? "rgba(68,136,255,0.12)" : undefined,
                }}
                onClick={() => toggleNotifMutation.mutate({ id: u.id, enabled: u.notifications_enabled })}
                disabled={toggleNotifMutation.isPending}
                title={u.notifications_enabled ? "Notifications ON" : "Notifications OFF"}
              >
                <FontAwesomeIcon icon={u.notifications_enabled ? faBell : faBellSlash} />
              </button>
              <button className="btn btn-ghost flex-1 justify-center" style={{ fontSize: 14, padding: "8px" }}
                onClick={() => setAssignUser(u)} title="Assign devices">
                <FontAwesomeIcon icon={faMicrochip} />
              </button>
              <button className="btn btn-ghost flex-1 justify-center" style={{ fontSize: 14, padding: "8px" }}
                onClick={() => setEditUser(u)} title="Edit">
                <FontAwesomeIcon icon={faEdit} />
              </button>
              <button className="btn btn-danger flex-1 justify-center" style={{ fontSize: 14, padding: "8px" }}
                onClick={() => { if (confirm(`Delete ${u.email}?`)) deleteMutation.mutate(u.id); }}>
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {addOpen && (
        <Modal title="Add User" onClose={() => setAddOpen(false)}>
          <UserForm
            onSubmit={(d) => createMutation.mutate(d)}
            onCancel={() => setAddOpen(false)}
            isSubmitting={createMutation.isPending}
          />
        </Modal>
      )}

      {editUser && (
        <Modal title={`Edit — ${editUser.email}`} onClose={() => setEditUser(null)}>
          <UserForm
            defaultValues={editUser}
            onSubmit={(d) => updateMutation.mutate({ id: editUser.id, data: d })}
            onCancel={() => setEditUser(null)}
            isSubmitting={updateMutation.isPending}
            onInvite={() => {
              if (confirm(`Send invitation email to ${editUser.email}?`)) inviteMutation.mutate(editUser.id);
            }}
            isInviting={inviteMutation.isPending}
          />
        </Modal>
      )}

      {assignUser && (
        <Modal title={`Devices — ${assignUser.full_name || assignUser.email}`} onClose={() => setAssignUser(null)}>
          <DeviceAssignment userId={assignUser.id} allDevices={allDevices} />
        </Modal>
      )}
    </div>
  );
}

function UserForm({ defaultValues, onSubmit, onCancel, isSubmitting, onInvite, isInviting }) {
  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(userSchema),
    defaultValues: defaultValues || { role: "user" },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <Field label="Email" name="email" register={register} type="email" error={errors.email} />
        </div>
        <Field label="Role" name="role" register={register} as="select">
          <option value="user">User</option>
          <option value="admin">Admin</option>
        </Field>
        <div className="hidden sm:block" /> {/* spacer */}
        <Field label="First Name" name="first_name" register={register} />
        <Field label="Last Name"  name="last_name"  register={register} />
        <div className="sm:col-span-2">
          <Field label="Phone (E.164)" name="phone_number" register={register} placeholder="+41..." />
        </div>
      </div>
      {defaultValues && (
        <div className="flex gap-5">
          <label className="flex items-center gap-3 data text-sm cursor-pointer" style={{ color: "var(--color-muted)" }}>
            <input type="checkbox" {...register("notifications_enabled")} />
            SMS notifications enabled
          </label>
          <label className="flex items-center gap-3 data text-sm cursor-pointer" style={{ color: "var(--color-muted)" }}>
            <input type="checkbox" {...register("active")} />
            Account active
          </label>
        </div>
      )}
      <div className="flex gap-3 pt-2">
        <button type="submit" disabled={isSubmitting} className="btn btn-cyan">
          {isSubmitting ? "Saving…" : defaultValues ? "Save" : "Create"}
        </button>
        {defaultValues && onInvite && (
          <button type="button" onClick={onInvite} disabled={isInviting} className="btn btn-ghost" style={{ gap: 6 }}>
            <FontAwesomeIcon icon={faPaperPlane} />
            {isInviting ? "Sending…" : "Send Invitation"}
          </button>
        )}
        <button type="button" onClick={onCancel} className="btn btn-ghost">Cancel</button>
      </div>
    </form>
  );
}

function DeviceAssignment({ userId, allDevices }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownRect, setDropdownRect] = useState(null);
  const inputRef = useRef(null);

  const { data: userData } = useQuery({
    queryKey: ["user-detail", userId],
    queryFn: () => api.get(`users/${userId}`).json(),
  });
  const userDevices = userData?.devices || [];
  const assignedIds = new Set(userDevices.map((d) => d.id));

  const assignMutation = useMutation({
    mutationFn: (deviceId) => api.post(`devices/${deviceId}/users`, { json: { user_id: userId } }).json(),
    onSuccess: () => { qc.invalidateQueries(["user-detail", userId]); setSearch(""); setShowDropdown(false); },
  });

  const removeMutation = useMutation({
    mutationFn: (deviceId) => api.delete(`devices/${deviceId}/users/${userId}`),
    onSuccess: () => qc.invalidateQueries(["user-detail", userId]),
  });

  const filtered = allDevices.filter((d) =>
    !assignedIds.has(d.id) &&
    (d.name.toLowerCase().includes(search.toLowerCase()) ||
     (d.dev_eui || "").toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="space-y-4">
      <div>
        <div className="data text-xs mb-2" style={{ color: "var(--color-muted)" }}>Assigned Devices</div>
        {userDevices.length === 0 ? (
          <p className="data text-sm" style={{ color: "var(--color-muted)" }}>No devices assigned</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {userDevices.map((d) => (
              <span key={d.id} className="flex items-center gap-2 data text-xs"
                style={{
                  background: "rgba(68,136,255,0.1)", border: "1px solid rgba(68,136,255,0.3)",
                  borderRadius: 100, padding: "4px 10px", color: "var(--color-white)",
                }}>
                {d.name}
                <button onClick={() => removeMutation.mutate(d.id)}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-muted)", fontSize: 12, padding: 0 }}>
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="data text-xs mb-2" style={{ color: "var(--color-muted)" }}>Add device</div>
        <div>
          <input ref={inputRef} className="input" placeholder="Search devices…" value={search}
            onChange={(e) => { setSearch(e.target.value); setShowDropdown(true); }}
            onFocus={() => { setDropdownRect(inputRef.current?.getBoundingClientRect()); setShowDropdown(true); }}
            onBlur={() => setTimeout(() => setShowDropdown(false), 150)} />
          {showDropdown && filtered.length > 0 && dropdownRect && (
            <div style={{
              position: "fixed",
              top: dropdownRect.bottom + 4,
              left: dropdownRect.left,
              width: dropdownRect.width,
              zIndex: 2000,
              background: "var(--color-raised)", border: "1px solid var(--color-border)",
              borderRadius: 6, maxHeight: 200, overflowY: "auto",
              boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
            }}>
              {filtered.map((d) => (
                <button key={d.id} onMouseDown={() => assignMutation.mutate(d.id)}
                  style={{
                    display: "block", width: "100%", textAlign: "left",
                    padding: "8px 12px", background: "none", border: "none", cursor: "pointer",
                    fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--color-white)",
                    borderBottom: "1px solid var(--color-border-dim)",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "rgba(68,136,255,0.1)"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "none"}>
                  {d.name}
                  {d.location_name && <span style={{ color: "var(--color-muted)", marginLeft: 8 }}>{d.location_name}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, name, register, type = "text", placeholder, as, children, error }) {
  return (
    <div>
      <label className="block mb-1 data text-xs" style={{ color: "var(--color-muted)", letterSpacing: "0.06em" }}>{label}</label>
      {as === "select" ? (
        <select {...register(name)} className="input">{children}</select>
      ) : (
        <input {...register(name)} type={type} className="input" placeholder={placeholder}
          style={error ? { borderColor: "var(--color-red)" } : undefined} />
      )}
      {error && <p className="mt-1 data text-xs" style={{ color: "var(--color-red)" }}>{error.message}</p>}
    </div>
  );
}
